import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ChapterHeading from "@/components/ChapterHeading";
import Lightbox from "@/components/Lightbox";
import CTABand from "@/components/CTABand";
import usePageMeta from "@/hooks/usePageMeta";
import useGallery from "@/hooks/useGallery";
import { useContent } from "@/content/ContentContext";
import { GALLERY_FILTERS } from "@/data/gallery";

export default function Gallery() {
  const { get } = useContent();
  usePageMeta(
    get("meta_gallery_title", "Our Work | Grab Rails, Hand Rails & Ramps Perth | Modify"),
    get("meta_gallery_desc", "See real Modify installations across Perth — grab rails, hand rails and rubber ramps fitted in local homes. Every photo is a genuine Modify job.")
  );
  const [filter, setFilter] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const allItems = useGallery();

  const items = useMemo(
    () => (filter === "all" ? allItems : allItems.filter((i) => i.category === filter)),
    [filter, allItems]
  );

  return (
    <div data-testid="gallery-page">
      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-44 sm:px-6 lg:px-8 lg:pt-40">
          <ChapterHeading
            num="01"
            eyebrow="Gallery"
            title="OUR RECENT WORK"
            copy="A look at some of the safety and accessibility solutions we've installed in real Perth homes."
            testid="gallery-heading"
          />

          <div className="mt-10 flex flex-wrap gap-2" role="group" aria-label="Filter gallery by service">
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => {
                  setFilter(f.id);
                  setLightboxIndex(null);
                }}
                data-testid={`gallery-filter-${f.id}`}
                aria-pressed={filter === f.id}
                className={`min-h-[48px] rounded-full border px-5 text-xs font-bold tracking-[0.12em] transition-colors duration-200 ${
                  filter === f.id
                    ? "border-charcoal bg-charcoal text-white"
                    : "border-line bg-white text-ink/70 hover:border-sage hover:text-charcoal"
                }`}
              >
                {f.label.toUpperCase()}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-10 columns-1 gap-5 sm:columns-2 lg:columns-3">
            <AnimatePresence>
              {items.map((item, i) => (
                <motion.button
                  layout
                  key={item.src}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  data-testid={`gallery-item-${i}`}
                  aria-label={`${item.alt} — open larger view`}
                  className="group mb-5 block w-full break-inside-avoid overflow-hidden rounded-2xl border border-line bg-white text-left"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.3) }}
                >
                  <span className="block overflow-hidden">
                    <img
                      src={item.src}
                      alt={item.alt}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </span>
                  <span className="block px-4 py-3 text-xs font-semibold text-ink/60">{item.alt}</span>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <Lightbox items={items} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={setLightboxIndex} />

      <CTABand
        title="LIKE WHAT YOU SEE?"
        copy="Tell us about your space and we'll help you find the right grab rail, hand rail or rubber ramp solution."
        testid="gallery-cta"
      />
    </div>
  );
}
